import React, { useState, useEffect } from 'react';

import { NotificationOnIcon, NotificationOffIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control, SettingsItemWrapper } from '@vlabs/uikit';
import cn from 'classnames';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { TelegramNotificationStatusButton } from '../../Cell';
import st from './SharedFormStyles.module.sass';

const allTargets = [
  'create_time',
  'event_id',
  'external_id',
  'source',
  'sample',
  'top_match_sample',
  'top_match_similarity',
  'gender',
  'age',
  'emotion',
  'mask',
  'tags',
  'temperature',
  'top_similar_object_user_data',
  'handler',
  'lists',
];

export function TelegramNotificationSubForm() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const {
    watch,
    register,
    setValue,
    getValues,
    reset,
    formState: { errors },
    version,
  } = useFormContext();

  const isEnable = watch('telegram.enable');

  const values = watch(allTargets.map((name) => `telegram.targets.${name}`));
  const isAllChecked = values.every(Boolean);

  const handleToggleAll = () => {
    allTargets.forEach((name) => {
      setValue(`telegram.targets.${name}`, !isAllChecked, { shouldDirty: true });
    });
  };

  const handleToggleEnable = () => {
    setValue('telegram.enable', !isEnable, { shouldDirty: true });
  };

  const recipientsValidator = (v) => {
    if (v && !v?.match(/^[0-9, -]+$/)) {
      return t('vlNotifier:validation.invalid_values');
    }
    return true;
  };

  useEffect(() => {
    const subscribe = watch((formData, { name }) => {
      if (name === 'telegram.enable' && formData.telegram.enable) {
        register('telegram.recipients', { required: validate.required() });
      }
      if (name === 'telegram.enable' && !formData.telegram.enable) {
        reset({ ...getValues(), 'telegram.recipients': undefined });
      }
    });
    return () => subscribe.unsubscribe();
  }, [watch, register, reset, getValues]);

  return (
    <div className={st.NotificationFormBlock}>
      <div className={st.Header}>
        <div className={st.HeaderTitle}>
          {isEnable
            ? <NotificationOnIcon className={st.NotificationIcon} />
            : <NotificationOffIcon className={st.NotificationIcon} />}
          <h6>Telegram</h6>
        </div>
        <TelegramNotificationStatusButton value={{ enable: isEnable, onClick: handleToggleEnable }} />
      </div>

      {isEnable && (
      <>
        <div className={st.Separator} />
        <SettingsItemWrapper
          className={cn(st.CustomSettingsItemWrapper, st.CustomSettingsItemWrapper_telegram)}
          contentClassName={st.Content}
          title={t('vlNotifier:telegram.recipients_short_label')}
          titleClassName={st.Title}
        >
          <Control.Input
            {...register('telegram.recipients', { validate: { recipientsValidator } })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <span className={st.DataSpan}>{t('vlNotifier:data_short_label')}</span>
        <div className={st.CheckboxContainer}>
          <Control.Checkbox
            checked={isAllChecked}
            id="telegram.targets.all"
            label={t('vlNotifier:targets.choose_all')}
            onChange={handleToggleAll}
          />
          <Control.Checkbox id="telegram.targets.create_time" label={t('vlNotifier:targets.create_time')} {...register('telegram.targets.create_time')} />
          <Control.Checkbox id="telegram.targets.event_id" label={t('vlNotifier:targets.event_id')} {...register('telegram.targets.event_id')} />
          <Control.Checkbox id="telegram.targets.external_id" label={t('vlNotifier:targets.external_id')} {...register('telegram.targets.external_id')} />
          <Control.Checkbox id="telegram.targets.source" label={t('vlNotifier:targets.source')} {...register('telegram.targets.source')} />
          <Control.Checkbox id="telegram.targets.sample" label={t('vlNotifier:targets.sample')} {...register('telegram.targets.sample')} />
          <Control.Checkbox id="telegram.targets.top_match_sample" label={t('vlNotifier:targets.top_match_sample')} {...register('telegram.targets.top_match_sample')} />
          <Control.Checkbox id="telegram.targets.top_match_similarity" label={t('vlNotifier:targets.top_match_similarity')} {...register('telegram.targets.top_match_similarity')} />
          <Control.Checkbox id="telegram.targets.gender" label={t('vlNotifier:targets.gender')} {...register('telegram.targets.gender')} />
          <Control.Checkbox id="telegram.targets.age" label={t('vlNotifier:targets.age')} {...register('telegram.targets.age')} />
          {!isVisible && (
            <Control.Link
              as="button"
              className={st.LinkAsButton}
              onClick={() => { setIsVisible(!isVisible); }}
              type="button"
            >
              {t('btn.show_more')}
            </Control.Link>
          )}
          {isVisible && (
            <>
              <Control.Checkbox id="telegram.targets.emotion" label={t('vlNotifier:targets.emotion')} {...register('telegram.targets.emotion')} />
              <Control.Checkbox id="telegram.targets.mask" label={t('vlNotifier:targets.mask')} {...register('telegram.targets.mask')} />
              <Control.Checkbox
                id="telegram.targets.tags"
                label={t('vlNotifier:targets.tags')}
                {...register('telegram.targets.tags')}
                disabled={!(version?.major >= 0 && version?.minor >= 1 && version?.patch >= 6)}
              />
              <Control.Checkbox
                id="telegram.targets.temperature"
                label={t('vlNotifier:targets.temperature')}
                {...register('telegram.targets.temperature')}
                disabled={!(version?.major >= 0 && version?.minor >= 1 && version?.patch >= 6)}
              />
              <Control.Checkbox id="telegram.targets.top_similar_object_user_data" label={t('vlNotifier:targets.top_similar_object_user_data')} {...register('telegram.targets.top_similar_object_user_data')} />
              <Control.Checkbox id="telegram.targets.handler" label={t('vlNotifier:targets.handler')} {...register('telegram.targets.handler')} />
              <Control.Checkbox id="telegram.targets.lists" label={t('vlNotifier:targets.lists')} {...register('telegram.targets.lists')} />
            </>
          )}
        </div>
      </>
      )}
    </div>
  );
}
